import { useState } from 'react';
import { useTheme } from './theme/ThemeProvider';
import type { ThemeMode } from './theme/theme';

const MODES: { value: ThemeMode; label: string }[] = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

/**
 * Theme segmented control + "Save to my account". Used by the satellite apps'
 * AppSettingsModal below and embedded in entitybox's SettingsModal Appearance
 * section. Selection applies instantly and persists on this device; saving to
 * the account makes other devices/apps without a local choice adopt it.
 */
export function ThemeSelector() {
  const { mode, setMode, canSaveToAccount } = useTheme();
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);

  const pick = (m: ThemeMode) => {
    setSaveState('idle');
    void setMode(m);
  };

  const saveToAccount = async () => {
    setSaveState('saving');
    setSaveError(null);
    try {
      await setMode(mode, { saveToAccount: true });
      setSaveState('saved');
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
      setSaveState('error');
    }
  };

  return (
    <div>
      <div className="inline-flex rounded-lg border border-neutral-300 p-0.5 dark:border-neutral-700">
        {MODES.map((m) => (
          <button
            key={m.value}
            onClick={() => pick(m.value)}
            className={`rounded-md px-3 py-1.5 text-sm transition ${
              mode === m.value
                ? 'bg-neutral-900 text-white dark:bg-neutral-200 dark:text-neutral-900'
                : 'text-neutral-600 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800'
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-3">
        <button
          onClick={saveToAccount}
          disabled={!canSaveToAccount || saveState === 'saving'}
          className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm text-neutral-700 transition hover:bg-neutral-100 disabled:opacity-50 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800"
        >
          {saveState === 'saving' ? 'Saving…' : 'Save to my account'}
        </button>
        {saveState === 'saved' && (
          <span className="text-xs text-green-700 dark:text-green-400">
            Saved — other EB apps will pick this up.
          </span>
        )}
        {saveState === 'error' && (
          <span className="text-xs text-red-600 dark:text-red-400">{saveError}</span>
        )}
      </div>
      <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">
        Theme is remembered on this device. Saving to your account applies it on devices where you
        haven't chosen one.
      </p>
    </div>
  );
}

/**
 * Settings modal for the satellite apps (comedy, marketing) — they have no
 * settings surface of their own. v1 content: appearance. Entitybox keeps its
 * own SettingsModal and embeds ThemeSelector instead.
 */
export function AppSettingsModal({
  isOpen,
  onClose,
  email,
}: {
  isOpen: boolean;
  onClose: () => void;
  email?: string | null;
}) {
  if (!isOpen) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 dark:bg-black/70"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-neutral-200 bg-white p-6 shadow-xl dark:border-neutral-800 dark:bg-neutral-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Settings</h2>
          <button
            onClick={onClose}
            aria-label="Close settings"
            className="rounded-md px-2 py-1 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700 dark:hover:bg-neutral-800 dark:hover:text-neutral-300"
          >
            ✕
          </button>
        </div>
        {email && (
          <p className="mb-4 text-xs text-neutral-500 dark:text-neutral-400">
            Signed in as <span className="font-mono">{email}</span>
          </p>
        )}
        <h3 className="mb-2 text-sm font-medium text-neutral-700 dark:text-neutral-300">Appearance</h3>
        <ThemeSelector />
      </div>
    </div>
  );
}
